import HeroSection from './components/HeroSection';
import ServicesSection from './components/ServicesSection';
import ExpertiseSection from './components/ExpertiseSection';
import DifferentiatorSection from './components/DifferentiatorSection';
import NewYorkShowcase from './components/NewYorkShowcase';
import AsiaMarketSection from './components/AsiaMarketSection';
import GlobalAffiliatesSection from './components/GlobalAffiliatesSection';
import AdvertisingShowcase from './components/AdvertisingShowcase';
import CampaignSuccessSection from './components/TeamSection';
import SocialMediaSection from './components/SocialMediaSection';
import PrivateJetSection from './components/PrivateJetSection';
import ContactSection from './components/ContactSection';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState, useEffect } from 'react';

const HomePage: React.FC = () => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Add event listener for contact modal trigger from header
  useEffect(() => {
    const handleOpenContactModal = () => {
      setShowContactModal(true);
    };

    window.addEventListener('openContactModal', handleOpenContactModal);
    
    return () => {
      window.removeEventListener('openContactModal', handleOpenContactModal);
    };
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', contactForm.name);
      formData.append('email', contactForm.email);
      formData.append('company', contactForm.company);
      formData.append('message', contactForm.message);
      formData.append('requestType', 'contact');

      const response = await fetch('https://readdy.ai/api/form/d497n2l92fv05nd9n0jg', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Form submission failed.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('An error occurred during submission. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBrandClick = (brand: string, category: string) => {
    window.REACT_APP_NAVIGATE(`/portal?brand=${encodeURIComponent(brand)}&category=${encodeURIComponent(category)}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <HeroSection />

      {/* Services Section */}
      <ServicesSection />

      {/* Expertise Section */}
      <ExpertiseSection />

      {/* Differentiator Section */}
      <DifferentiatorSection />

      {/* Advertising Showcase */}
      <AdvertisingShowcase />

      {/* New York Showcase */}
      <NewYorkShowcase />

      {/* Asia Market Section */}
      <AsiaMarketSection />

      {/* Team Section */}
      <CampaignSuccessSection />

      {/* Global Affiliates Section */}
      <GlobalAffiliatesSection />

      {/* Social Media Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Follow Our Global Channels
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto">
            Stay connected with our latest campaigns, industry insights, and global partnerships across all major platforms.
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 mb-12">
            {[
              { name: "Instagram", icon: "ri-instagram-line", followers: "2.8M", engagement: "12.4%" },
              { name: "LinkedIn", icon: "ri-linkedin-line", followers: "850K", engagement: "8.7%" },
              { name: "Facebook", icon: "ri-facebook-line", followers: "1.2M", engagement: "6.3%" },
              { name: "YouTube", icon: "ri-youtube-line", followers: "420K", engagement: "15.2%" },
              { name: "X", icon: "ri-twitter-x-line", followers: "680K", engagement: "9.1%" },
              { name: "TikTok", icon: "ri-tiktok-line", followers: "1.5M", engagement: "18.6%" },
              { name: "WhatsApp", icon: "ri-whatsapp-line", followers: "Business", engagement: "24/7" },
              { name: "WeChat", icon: "ri-wechat-line", followers: "320K", engagement: "11.8%" }
            ].map((platform) => (
              <div
                key={platform.name}
                className="group bg-gray-50 rounded-2xl p-6 hover:bg-blue-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors shadow-sm mx-auto">
                  <i className={`${platform.icon} text-2xl text-gray-700 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                  {platform.name}
                </h3>
                <div className="space-y-1">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{platform.followers}</span> followers
                  </div>
                  <div className="text-xs text-blue-600 font-medium">
                    {platform.engagement} engagement
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowContactModal(true)}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Subscribe to Updates
            </button>
            <button className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer">
              View Portfolio
            </button>
          </div>
        </div>
      </section>

      {/* Private Jet VVIP Section */}
      <section className="relative py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-black via-gray-900 to-slate-800 overflow-hidden">
        {/* Luxury Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/10 to-transparent"></div>
        <div className="absolute top-20 right-20 w-32 h-32 bg-gradient-to-br from-yellow-400 to-gold-500 rounded-full opacity-10 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-br from-silver-300 to-gray-400 rounded-full opacity-5 animate-bounce" style={{ animationDuration: '4s' }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Private Jet VVIP Advertising
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Exclusive advertising solutions targeting ultra-high-net-worth individuals through private aircraft cabins, VIP lounges, and executive terminals worldwide.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-gold-500 rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-plane-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center">Private Jet Cabins</h3>
              <p className="text-gray-300 text-center leading-relaxed">
                Premium brand placements inside luxury aircraft targeting billionaires and C-suite executives during international flights.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-silver-300 to-gray-400 rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-vip-crown-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center">VIP Lounges</h3>
              <p className="text-gray-300 text-center leading-relaxed">
                Exclusive advertising spaces in first-class lounges at major international airports across GCC and Asia.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-building-4-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center">Executive Terminals</h3>
              <p className="text-gray-300 text-center leading-relaxed">
                Strategic placements in private terminals and FBOs frequented by global business leaders and luxury travelers.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-8">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h4 className="text-lg font-semibold text-yellow-400 mb-4">Target Audience Profile</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white mb-1">$50M+</div>
                    <div className="text-sm text-gray-400">Average Net Worth</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white mb-1">Ultra-Elite</div>
                    <div className="text-sm text-gray-400">Audience Tier</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white mb-1">500+</div>
                    <div className="text-sm text-gray-400">Private Jets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white mb-1">50+</div>
                    <div className="text-sm text-gray-400">VIP Lounges</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setShowContactModal(true)}
                  className="bg-gradient-to-r from-yellow-500 to-gold-600 text-black px-8 py-4 rounded-xl font-bold hover:shadow-2xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Book VVIP Consultation
                </button>
                <button 
                  onClick={() => window.REACT_APP_NAVIGATE('/elite-portfolio')}
                  className="border-2 border-yellow-400 text-yellow-400 px-8 py-4 rounded-xl font-semibold hover:bg-yellow-400/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  View Elite Portfolio
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] bg-gradient-to-br from-yellow-600/20 to-silver-600/20 rounded-2xl backdrop-blur-sm border border-white/10 overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/30f9d60a3efd60664be8cff8b5660f43.jpg"
                  alt="Private Jet VVIP Interior"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6">Luxury Brand Partners</h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Trusted by the world's most prestigious brands for exclusive VVIP audience targeting.
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
              {[
                { name: "Rolex", category: "Luxury Watches" },
                { name: "Louis Vuitton", category: "Fashion & Leather Goods" },
                { name: "Ferrari", category: "Luxury Automotive" },
                { name: "Bentley", category: "Premium Automotive" },
                { name: "Cartier", category: "Jewelry & Watches" },
                { name: "Hermès", category: "Luxury Fashion" }
              ].map((brand) => (
                <div 
                  key={brand.name}
                  onClick={() => handleBrandClick(brand.name, brand.category)}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all cursor-pointer group"
                >
                  <div className="text-gray-400 hover:text-yellow-400 transition-colors duration-300 text-sm sm:text-base lg:text-lg font-light">
                    {brand.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 group-hover:text-gray-400 transition-colors">
                    {brand.category}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <Footer />

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Contact Us</h2>
              <button 
                onClick={() => setShowContactModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer text-xl"
              >
                ✕
              </button>
            </div>
            
            {!submitted ? (
              <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    name="company"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={contactForm.company}
                    onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    name="message"
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">{contactForm.message.length}/500 characters</div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Message Sent!</h3>
                <p className="text-gray-600 mb-4">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => {
                    setShowContactModal(false);
                    setSubmitted(false);
                    setContactForm({ name: "", email: "", company: "", message: "" });
                  }}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
